// Wave C ACON-2 (Member Dashboard design, Company Contacts, ACC:L317-641):
// the contact cards the Primary Contact manages. Field values live in the
// profile form; the Accounts Payable, Marketing, HR and Sales cards save to
// cmpExtension, the Administrator card to the existing administrator fields.

export type CmpContactFields = { name: string; title: string; email: string; phone: string; ext: string; mobile: string };
export type CmpBillingAddress = { streetAddress: string; city: string; province: string; postalCode: string; country: string };
export type CmpExtensionContactId = 'accountsPayable' | 'marketingCommunication' | 'hrEmployeeEngagement' | 'sales';
export type CmpContactCardId = CmpExtensionContactId | 'administrator';

export const CONTACT_FIELD_KEYS: (keyof CmpContactFields)[] = ['name', 'title', 'email', 'phone', 'ext', 'mobile'];

export type CmpContactCard = {
  contactId: CmpContactCardId;
  title: string;
  intro: string;
  /** Formik field names for each contact field. */
  fieldNames: Record<keyof CmpContactFields, string>;
  billingAddress?: boolean;
  /** D8: only the Accounts Payable card stars Contact Name and Contact Email. */
  requiredStars?: boolean;
  /** D6/D7 text next to the same-as-primary box. */
  sameAsPrimaryLabel: string;
};

const SAME_AS_PRIMARY_LABEL = 'If you do not have the contact name and email below, check to add the primary contact or leave blank.';

const extensionFieldNames = (contactId: CmpExtensionContactId): Record<keyof CmpContactFields, string> => ({
  name: `cmpContacts.${contactId}.name`, title: `cmpContacts.${contactId}.title`, email: `cmpContacts.${contactId}.email`,
  phone: `cmpContacts.${contactId}.phone`, ext: `cmpContacts.${contactId}.ext`, mobile: `cmpContacts.${contactId}.mobile`,
});

export const CMP_CONTACT_CARDS: CmpContactCard[] = [
  { contactId: 'accountsPayable', title: 'Accounts Payable Contact and Billing/Mailing Address',
    intro: 'Update the contact responsible for membership invoices and payments.',
    fieldNames: extensionFieldNames('accountsPayable'), billingAddress: true, requiredStars: true,
    sameAsPrimaryLabel: 'If you do not have the contact name and email below, check to add the primary contact.' },
  { contactId: 'administrator', title: 'Company Administrator Contact',
    intro: 'Update the Administrator Contact if you would like to provide an additional point of contact for your company.',
    fieldNames: { name: 'adminConsoleContactName', title: 'cpaAdminCompanyAdminContactTitle', email: 'adminConsoleLoginEmail',
      phone: 'cpaAdminCompanyAdminContactNumber', ext: 'cpaAdminCompanyAdminContactNumberExtension',
      mobile: 'cpaAdminCompanyAdminContactMobilePhoneNumber' }, sameAsPrimaryLabel: SAME_AS_PRIMARY_LABEL },
  { contactId: 'marketingCommunication', title: 'Marketing & Communications Contact',
    intro: 'Update the contact responsible for your company’s marketing and communications. This person will receive promotions, sponsorship opportunities, and other member communications to help your company maximize its membership value.',
    fieldNames: extensionFieldNames('marketingCommunication'), sameAsPrimaryLabel: SAME_AS_PRIMARY_LABEL },
  { contactId: 'hrEmployeeEngagement', title: 'HR/Employee Engagement Contact',
    intro: 'Update the contact who shares membership benefits and opportunities with your employees. This person will help promote cost-saving programs, events, resources, and the value of downloading the Chamber Perks App™ to access local and national discounts.',
    fieldNames: extensionFieldNames('hrEmployeeEngagement'), sameAsPrimaryLabel: SAME_AS_PRIMARY_LABEL },
  { contactId: 'sales', title: 'Sales/Business Development Contact',
    intro: 'Update the contact responsible for business development, growth, and sales opportunities. This person will receive information about networking opportunities, partnerships, events, and chamber initiatives designed to increase visibility, build relationships, and support business growth.',
    fieldNames: extensionFieldNames('sales'), sameAsPrimaryLabel: SAME_AS_PRIMARY_LABEL },
];

export const CONTACTS_NOT_IN_DIRECTORY = 'This contact information will NOT appear on our website directory or in the Chamber Perks App™ global directories, unless it is entered as the main company contact.';
export const GRANT_LABEL = 'Check this box to grant this contact access to your member dashboard. Based on their roles, they will be allowed to view and update various sections such as company profile on the Chamber website and in the Chamber Perks App™ Global Directory, create and manage company perks, view and pay invoices, register for events and update attendee details. Click the Send Credentials button.';
export const SAME_AS_PRIMARY_WARNING = 'Please fill up the Company Primary Contact details first.';
export const SAME_AS_PRIMARY_HINT = 'This contact is the same as the Company Primary Contact, who already has access to everything in the Member Dashboard. Enter a different contact to grant access.';
/** D13: shown after a save or send attempt on a granted card without a name and email. */
export const INCOMPLETE_CONTACT_HINT = 'Please enter the Contact Name and Contact Email to grant this contact access to the Member Dashboard.';
/** D9: shown under Send Credentials while the card has unsaved changes. */
export const SAVE_FIRST_HINT = 'Save changes to this section before sending credentials.';
export const BILLING_HEADING = 'Billing/Mailing Address';
/** Design (4) CMA:2221 bill_notice; shown only while the billing address is the company address (M2). */
export const BILLING_NOTICE = 'Update the billing/mailing address if it is different from your company address';

/** Form field that holds a grant tick waiting for Save Changes (D12). Never sent to the backend. */
export const cmpPendingGrantPath = (contactId: CmpContactCardId) => `cmpPendingGrant.${contactId}`;
const noPendingGrant = (): Record<CmpContactCardId, boolean> => ({
  accountsPayable: false, administrator: false, marketingCommunication: false, hrEmployeeEngagement: false, sales: false,
});

const EXTENSION_PREFIX: Record<CmpExtensionContactId, string> = {
  accountsPayable: 'accountsPayable', marketingCommunication: 'marketingCommunication',
  hrEmployeeEngagement: 'hrEmployeeEngagement', sales: 'salesBusinessDevelopment',
};
const EXTENSION_IDS = Object.keys(EXTENSION_PREFIX) as CmpExtensionContactId[];

type Extension = Record<string, unknown> | null | undefined;
type SavedMember = Record<string, unknown> & { cmpExtension?: Extension };

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const emptyContact = (): CmpContactFields => ({ name: '', title: '', email: '', phone: '', ext: '', mobile: '' });

const extensionContact = (extension: Extension, contactId: CmpExtensionContactId): CmpContactFields => {
  const prefix = EXTENSION_PREFIX[contactId];
  const value = (suffix: string) => text(extension?.[`${prefix}Contact${suffix}`]);
  return { name: value('Name'), title: value('Title'), email: value('Email'), phone: value('Number'),
    ext: value('NumberExtension'), mobile: value('MobilePhoneNumber') };
};

/** Form values for the four cmpExtension cards. G-3: an empty HR card starts from the Employee Adoption contact. */
export const getCmpContactFormValues = (member: SavedMember | null | undefined) => {
  const extension = member?.cmpExtension;
  const contacts = EXTENSION_IDS.reduce((result, contactId) => ({ ...result, [contactId]: extensionContact(extension, contactId) }),
    {} as Record<CmpExtensionContactId, CmpContactFields>);
  if (!contacts.hrEmployeeEngagement.name && !contacts.hrEmployeeEngagement.email && member) {
    contacts.hrEmployeeEngagement = {
      name: text(member.cpaEmployeeAdoptionContactName), title: text(member.cpaEmployeeAdoptionContactTitle),
      email: text(member.cpaEmployeeAdoptionContactEmail), phone: text(member.cpaEmployeeAdoptionContactNumber),
      ext: text(member.cpaEmployeeAdoptionContactNumberExtension), mobile: text(member.cpaEmployeeAdoptionContactMobilePhoneNumber),
    };
  }
  const billing = (extension?.accountsPayableBillingAddress || {}) as Record<string, unknown>;
  return {
    cmpContacts: contacts,
    cmpBillingAddress: { streetAddress: text(billing.streetAddress), city: text(billing.city), province: text(billing.province),
      postalCode: text(billing.postalCode), country: text(billing.country) } as CmpBillingAddress,
    cmpPendingGrant: noPendingGrant(),
  };
};

export const EMPTY_CMP_CONTACT_FORM_VALUES = {
  cmpContacts: EXTENSION_IDS.reduce((result, contactId) => ({ ...result, [contactId]: emptyContact() }),
    {} as Record<CmpExtensionContactId, CmpContactFields>),
  cmpBillingAddress: { streetAddress: '', city: '', province: '', postalCode: '', country: '' } as CmpBillingAddress,
  cmpPendingGrant: noPendingGrant(),
};

const orNull = (value: string | undefined): string | null => (value || '').trim() || null;

/** The partial cmpExtension the profile save sends (only the contact cards). */
export const buildCmpContactExtensionInput = (values: {
  cmpContacts: Record<CmpExtensionContactId, CmpContactFields>; cmpBillingAddress: CmpBillingAddress;
}) => {
  const input: Record<string, unknown> = {};
  EXTENSION_IDS.forEach(contactId => {
    const prefix = EXTENSION_PREFIX[contactId];
    const contact = values.cmpContacts[contactId] || emptyContact();
    input[`${prefix}ContactName`] = orNull(contact.name);
    input[`${prefix}ContactTitle`] = orNull(contact.title);
    input[`${prefix}ContactEmail`] = orNull(contact.email) && contact.email.trim().toLowerCase();
    input[`${prefix}ContactNumber`] = orNull(contact.phone);
    input[`${prefix}ContactNumberExtension`] = orNull(contact.ext);
    input[`${prefix}ContactMobilePhoneNumber`] = orNull(contact.mobile);
  });
  const billing = values.cmpBillingAddress;
  input.accountsPayableBillingAddress = {
    streetAddress: orNull(billing.streetAddress), city: orNull(billing.city), province: orNull(billing.province),
    postalCode: orNull(billing.postalCode), country: orNull(billing.country),
  };
  return input;
};

const getPath = (values: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((value, key) => (value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined), values);
const getIn = (values: unknown, path: string): string => text(getPath(values, path));

/** True when the card has a grant tick waiting for Save Changes. */
export const hasPendingGrant = (values: unknown, contactId: CmpContactCardId) => getPath(values, cmpPendingGrantPath(contactId)) === true;

export const getCardFields = (values: unknown, card: CmpContactCard): CmpContactFields =>
  CONTACT_FIELD_KEYS.reduce((result, key) => ({ ...result, [key]: getIn(values, card.fieldNames[key]) }), emptyContact());

export const getPrimaryContactFields = (values: unknown): CmpContactFields => ({
  name: getIn(values, 'primaryContactName'), title: getIn(values, 'primaryContactTitle'), email: getIn(values, 'primaryContactEmail'),
  phone: getIn(values, 'primaryContactPhoneNumber'), ext: getIn(values, 'primaryContactPhoneNumberExtension'),
  mobile: getIn(values, 'primaryContactMobilePhoneNumber'),
});

const normalize = (value: string) => value.trim().toLowerCase();
export const isValidContactEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());
export const isCompleteContact = (fields: CmpContactFields) => !!fields.name.trim() && isValidContactEmail(fields.email);
/** Design matchesPrimary: every field equals the Primary Contact. */
export const isSameAsPrimary = (fields: CmpContactFields, primary: CmpContactFields) =>
  !!normalize(primary.name) && !!normalize(primary.email) && CONTACT_FIELD_KEYS.every(key => normalize(fields[key]) === normalize(primary[key]));

/** "Oct 12, 2026 at 3:04 PM" in the chamber time zone. */
export const formatContactDate = (utcMilli: number | null | undefined, timeZone?: string | null): string | null => {
  if (!utcMilli) return null;
  const date = new Date(utcMilli);
  const format = (zone?: string) => {
    const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: zone });
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: zone });
    return `${day} at ${time}`;
  };
  try { return format(timeZone || undefined); } catch { return format(undefined); }
};

export const buildCredentialsText = ({ chamberName, name, email, password, loginUrl }: {
  chamberName: string; name: string; email: string; password: string; loginUrl: string;
}) => ['Member Dashboard Login Credentials', chamberName, '', `Name: ${name}`, `Username: ${email}`,
  `Temporary Password: ${password}`, `Login: ${loginUrl}`, '', 'You will be asked to set a new password at first login.'].join('\n');
